import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Users,
  Trophy,
  Medal,
  Award,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  Trash2,
  Clock,
  User,
  ChevronRight,
  Calendar,
  Briefcase,
  Save,
  Star,
  Target,
  ExternalLink,
  Pencil
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";
import { EmailThreadsPanel } from "../components/EmailThreadsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  Gold: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200", icon: Trophy },
  Silver: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", icon: Medal },
  Bronze: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200", icon: Award },
  Unranked: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-100", icon: Users }
};

const CONTACT_REMARK_COLORS = {
  green: { bg: "bg-green-100", text: "text-green-800", label: "Responded/Interested" },
  yellow: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending/Follow-up" },
  red: { bg: "bg-red-100", text: "text-red-800", label: "Not Interested" },
  blue: { bg: "bg-blue-100", text: "text-blue-800", label: "Meeting Scheduled" },
  purple: { bg: "bg-purple-100", text: "text-purple-800", label: "Active Opportunity" }
};

const TierBadge = ({ tier, size = "default" }) => {
  const style = TIER_STYLES[tier] || TIER_STYLES.Unranked;
  const Icon = style.icon;
  return (
    <Badge className={cn(
      "gap-1.5", 
      style.bg, 
      style.text, 
      "border", 
      style.border,
      size === "large" && "text-base px-4 py-2"
    )}>
      <Icon className={size === "large" ? "h-5 w-5" : "h-4 w-4"} />
      {tier} Tier
    </Badge>
  );
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "short", 
      day: "numeric"
    });
  } catch {
    return dateStr;
  }
};

// Inline edit form for studies
const StudyEditForm = ({ study, onSave, onCancel }) => {
  const [editData, setEditData] = useState({
    study_name: study.study_name || "",
    protocol_number: study.protocol_number || "",
    sponsor: study.sponsor || "",
    therapeutic_area: study.therapeutic_area || "",
    status: study.status || "Potential",
    feasibility_received_date: study.feasibility_received_date || "",
    feasibility_completed_date: study.feasibility_completed_date || "",
    start_date: study.start_date || "",
    end_date: study.end_date || "",
    notes: study.notes || "",
    retrospective_notes: study.retrospective_notes || "",
    responsiveness_rating: study.responsiveness_rating || ""
  });

  const isConcluded = editData.status === "Concluded";

  return (
    <div className="space-y-3" data-testid="study-edit-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Study Name</Label>
          <Input
            value={editData.study_name}
            onChange={(e) => setEditData(prev => ({ ...prev, study_name: e.target.value }))}
            className="mt-1 h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
            <SelectTrigger className="mt-1 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Ongoing">🟢 Ongoing</SelectItem>
              <SelectItem value="Actively Pursuing">🟡 Actively Pursuing</SelectItem>
              <SelectItem value="Potential">🔵 Potential</SelectItem>
              <SelectItem value="Concluded">⚫ Concluded</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Protocol #</Label>
          <Input
            value={editData.protocol_number}
            onChange={(e) => setEditData(prev => ({ ...prev, protocol_number: e.target.value }))}
            className="mt-1 h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Sponsor</Label>
          <Input
            value={editData.sponsor}
            onChange={(e) => setEditData(prev => ({ ...prev, sponsor: e.target.value }))}
            className="mt-1 h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Start Date</Label>
          <Input
            type="date"
            value={editData.start_date}
            onChange={(e) => setEditData(prev => ({ ...prev, start_date: e.target.value }))}
            className="mt-1 h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">End Date</Label>
          <Input
            type="date"
            value={editData.end_date}
            onChange={(e) => setEditData(prev => ({ ...prev, end_date: e.target.value }))}
            className="mt-1 h-8 text-sm"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Notes</Label>
        <Textarea
          value={editData.notes}
          onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
          className="mt-1 text-sm"
          rows={2}
        />
      </div>
      {isConcluded && (
        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-3">
          <p className="text-xs font-semibold text-slate-600">Retrospective Evaluation (Concluded Trials)</p>
          <div>
            <Label className="text-xs">Responsiveness Rating (1-5)</Label>
            <Select value={String(editData.responsiveness_rating || "")} onValueChange={(v) => setEditData(prev => ({ ...prev, responsiveness_rating: v ? parseInt(v) : null }))}>
              <SelectTrigger className="mt-1 h-8 text-sm">
                <SelectValue placeholder="Rate 1-5" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">⭐⭐⭐⭐⭐ Excellent</SelectItem>
                <SelectItem value="4">⭐⭐⭐⭐ Good</SelectItem>
                <SelectItem value="3">⭐⭐⭐ Average</SelectItem>
                <SelectItem value="2">⭐⭐ Poor</SelectItem>
                <SelectItem value="1">⭐ Very Poor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Retrospective Notes (responsiveness, interaction quality, lessons learned)</Label>
            <Textarea
              value={editData.retrospective_notes}
              onChange={(e) => setEditData(prev => ({ ...prev, retrospective_notes: e.target.value }))}
              className="mt-1 text-sm"
              rows={3}
              placeholder="e.g., Highly responsive team, completed feasibility quickly. Would work with them again."
            />
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" data-testid="study-edit-save-btn" onClick={() => onSave(editData)} className="bg-purple-600 hover:bg-purple-700">
          <Save className="h-3 w-3 mr-1" /> Save
        </Button>
        <Button size="sm" variant="outline" data-testid="study-edit-cancel-btn" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

// Study status config
const STUDY_STATUS_CONFIG = {
  "Ongoing": { color: "emerald", emoji: "🟢", label: "Ongoing" },
  "Actively Pursuing": { color: "amber", emoji: "🟡", label: "Actively Pursuing" },
  "Potential": { color: "blue", emoji: "🔵", label: "Potential" },
  "Concluded": { color: "slate", emoji: "⚫", label: "Concluded" }
};

export default function CompanyDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  
  // CSM State
  const [csm, setCsm] = useState({
    last_meeting_date: "",
    meeting_type: "",
    csm_notes_history: []
  });
  const [newCsmNote, setNewCsmNote] = useState("");
  const [savingCsm, setSavingCsm] = useState(false);
  const [expandedCsmNoteIdx, setExpandedCsmNoteIdx] = useState(null);

  // Studies State
  const [studies, setStudies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [showAddStudy, setShowAddStudy] = useState(false);
  const [newStudy, setNewStudy] = useState({
    study_name: "",
    protocol_number: "",
    sponsor: "",
    therapeutic_area: "",
    status: "Potential",
    feasibility_received_date: "",
    feasibility_completed_date: "",
    start_date: "",
    end_date: "",
    notes: ""
  });
  const [addingStudy, setAddingStudy] = useState(false);
  const [editingStudyId, setEditingStudyId] = useState(null);

  // Tier editing
  const [editingTier, setEditingTier] = useState(false);
  const [selectedTier, setSelectedTier] = useState("");

  const user = JSON.parse(localStorage.getItem("stryde_user") || "{}");
  const companyName = decodeURIComponent(name);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, csmRes, studiesRes, oppsRes] = await Promise.all([
          axios.get(`${API}/companies/${encodeURIComponent(companyName)}/detail`),
          axios.get(`${API}/companies/${encodeURIComponent(companyName)}/csm`),
          axios.get(`${API}/companies/${encodeURIComponent(companyName)}/studies`),
          axios.get(`${API}/opportunities/by-company/${encodeURIComponent(companyName)}`).catch(() => ({ data: [] }))
        ]);
        setCompany(companyRes.data);
        setNotes(companyRes.data.notes || []);
        setCsm(csmRes.data || {});
        setStudies(studiesRes.data || []);
        setOpportunities(oppsRes.data || []);
        setSelectedTier(companyRes.data.tier || "Unranked");
      } catch (error) {
        console.error("Fetch error:", error);
        toast.error("Company not found");
        navigate("/companies");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyName, navigate]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      const response = await axios.post(`${API}/companies/${encodeURIComponent(companyName)}/notes`, {
        text: newNote,
        created_by: user.name || "User"
      });
      setNotes(prev => [response.data, ...prev]);
      setNewNote("");
      toast.success("Note added");
    } catch (error) {
      console.error("Add note error:", error);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/companies/${encodeURIComponent(companyName)}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success("Note deleted");
    } catch (error) {
      console.error("Delete note error:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleSaveCsm = async () => {
    setSavingCsm(true);
    try {
      const payload = {
        last_meeting_date: csm.last_meeting_date || null,
        meeting_type: csm.meeting_type || null,
        csm_notes: newCsmNote.trim() || null
      };
      const response = await axios.put(`${API}/companies/${encodeURIComponent(companyName)}/csm`, payload);
      setCsm(response.data || {});
      setNewCsmNote("");
      toast.success(newCsmNote.trim() ? "CSM note added" : "Customer Success info saved");
    } catch (error) {
      console.error("Save CSM error:", error);
      toast.error("Failed to save");
    } finally {
      setSavingCsm(false);
    }
  };

  const handleDeleteCsmNote = async (noteId) => {
    if (!noteId) return;
    try {
      await axios.delete(`${API}/companies/${encodeURIComponent(companyName)}/csm/notes/${noteId}`);
      const refreshed = await axios.get(`${API}/companies/${encodeURIComponent(companyName)}/csm`);
      setCsm(refreshed.data || {});
      toast.success("CSM note deleted");
    } catch (error) {
      console.error("Delete CSM note error:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleUpdateTier = async (newTier) => {
    try {
      await axios.put(`${API}/companies/${encodeURIComponent(companyName)}/tier`, { tier: newTier });
      setSelectedTier(newTier);
      setCompany(prev => ({ ...prev, tier: newTier }));
      setEditingTier(false);
      toast.success(`Tier updated to ${newTier} for all contacts`);
    } catch (error) {
      console.error("Update tier error:", error);
      toast.error("Failed to update tier");
    }
  };

  const handleAddStudy = async () => {
    if (!newStudy.study_name.trim()) {
      toast.error("Study name is required");
      return;
    }
    
    setAddingStudy(true);
    try {
      const response = await axios.post(`${API}/companies/${encodeURIComponent(companyName)}/studies`, newStudy);
      setStudies(prev => [response.data, ...prev]);
      setNewStudy({
        study_name: "",
        protocol_number: "",
        sponsor: "",
        therapeutic_area: "",
        status: "Potential",
        feasibility_received_date: "",
        feasibility_completed_date: "",
        start_date: "",
        end_date: "",
        notes: ""
      });
      setShowAddStudy(false);
      toast.success("Study added");
    } catch (error) {
      console.error("Add study error:", error);
      toast.error("Failed to add study");
    } finally {
      setAddingStudy(false);
    }
  };

  const handleUpdateStudy = async (studyId, updateData) => {
    try {
      // Coerce empty strings to null so Pydantic Optional[int]/Optional[str] accepts them
      const cleaned = {};
      Object.entries(updateData).forEach(([k, v]) => {
        if (v === "" || v === undefined) {
          cleaned[k] = null;
        } else if (k === "responsiveness_rating" && typeof v === "string") {
          cleaned[k] = parseInt(v, 10) || null;
        } else {
          cleaned[k] = v;
        }
      });
      const response = await axios.put(`${API}/companies/${encodeURIComponent(companyName)}/studies/${studyId}`, cleaned);
      setStudies(prev => prev.map(s => s.id === studyId ? response.data : s));
      setEditingStudyId(null);
      toast.success("Study updated");
    } catch (error) {
      console.error("Update study error:", error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(", ") : (detail || "Failed to update study");
      toast.error(msg);
    }
  };

  const handleDeleteStudy = async (studyId) => {
    try {
      await axios.delete(`${API}/companies/${encodeURIComponent(companyName)}/studies/${studyId}`);
      setStudies(prev => prev.filter(s => s.id !== studyId));
      toast.success("Study deleted");
    } catch (error) {
      console.error("Delete study error:", error);
      toast.error("Failed to delete study");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="space-y-6" data-testid="company-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/companies">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Companies
            </Button>
          </Link>
        </div>
      </div>

      {/* Company Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-slate-100 rounded-xl">
                <Building2 className="h-10 w-10 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">{company.name}</CardTitle>
                <div className="flex items-center gap-3 mt-2">
                  {editingTier ? (
                    <Select value={selectedTier} onValueChange={handleUpdateTier}>
                      <SelectTrigger className="w-[140px]" data-testid="tier-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Gold">🏆 Gold</SelectItem>
                        <SelectItem value="Silver">🥈 Silver</SelectItem>
                        <SelectItem value="Bronze">🥉 Bronze</SelectItem>
                        <SelectItem value="Unranked">Unranked</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <button onClick={() => setEditingTier(true)} className="hover:opacity-80 transition-opacity">
                      <TierBadge tier={company.tier} size="large" />
                    </button>
                  )}
                  {editingTier && (
                    <Button variant="ghost" size="sm" onClick={() => setEditingTier(false)}>
                      Cancel
                    </Button>
                  )}
                  {!editingTier && (
                    <span className="text-xs text-slate-400">(click to edit tier)</span>
                  )}
                  <Badge variant="outline" className="text-sm">
                    <Users className="h-3 w-3 mr-1" />
                    {company.lead_count} contacts
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {company.status_breakdown && Object.keys(company.status_breakdown).length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-slate-500 mr-2">Status:</span>
              {Object.entries(company.status_breakdown).map(([status, count]) => (
                <Badge key={status} variant="outline" className="text-xs">
                  {status}: {count}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Studies & CSM */}
        <div className="lg:col-span-2 space-y-6">
          {/* Studies Card (includes Feasibility) */}
          <Card className="border-purple-200 bg-purple-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-purple-600" />
                    Studies & Feasibilities
                  </CardTitle>
                  <CardDescription>Track feasibility requests and active studies</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAddStudy(!showAddStudy)}
                  className="border-purple-200 hover:bg-purple-100"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Study
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {showAddStudy && (
                <div className="p-4 border border-purple-200 rounded-lg bg-white space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Study Name *</Label>
                      <Input
                        value={newStudy.study_name}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, study_name: e.target.value }))}
                        placeholder="Enter study name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Protocol Number</Label>
                      <Input
                        value={newStudy.protocol_number}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, protocol_number: e.target.value }))}
                        placeholder="e.g., ABC-123"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Sponsor</Label>
                      <Input
                        value={newStudy.sponsor}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, sponsor: e.target.value }))}
                        placeholder="Sponsor name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Therapeutic Area</Label>
                      <Input
                        value={newStudy.therapeutic_area}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, therapeutic_area: e.target.value }))}
                        placeholder="e.g., Oncology"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Status</Label>
                      <Select value={newStudy.status} onValueChange={(v) => setNewStudy(prev => ({ ...prev, status: v }))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Ongoing">🟢 Ongoing</SelectItem>
                          <SelectItem value="Actively Pursuing">🟡 Actively Pursuing</SelectItem>
                          <SelectItem value="Potential">🔵 Potential</SelectItem>
                          <SelectItem value="Concluded">⚫ Concluded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Feasibility Received</Label>
                      <Input
                        type="date"
                        value={newStudy.feasibility_received_date}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, feasibility_received_date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Feasibility Completed</Label>
                      <Input
                        type="date"
                        value={newStudy.feasibility_completed_date}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, feasibility_completed_date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Study Start Date</Label>
                      <Input
                        type="date"
                        value={newStudy.start_date}
                        onChange={(e) => setNewStudy(prev => ({ ...prev, start_date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Notes</Label>
                    <Textarea
                      value={newStudy.notes}
                      onChange={(e) => setNewStudy(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes about this study..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddStudy} disabled={addingStudy} className="bg-purple-600 hover:bg-purple-700">
                      {addingStudy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                      Add Study
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddStudy(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {studies.length === 0 && !showAddStudy ? (
                <p className="text-sm text-slate-500 text-center py-4">No studies or feasibilities recorded yet. Click &quot;Add Study&quot; to start tracking.</p>
              ) : (
                <div className="space-y-5">
                  {["Ongoing", "Actively Pursuing", "Potential", "Concluded"].map((statusKey) => {
                    const list = studies.filter(s => (s.status || "Potential") === statusKey);
                    if (list.length === 0) return null;
                    const cfg = STUDY_STATUS_CONFIG[statusKey] || { color: "slate", emoji: "•", label: statusKey };
                    return (
                      <div key={statusKey} data-testid={`study-section-${statusKey.replace(/\s+/g,'-').toLowerCase()}`}>
                        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                          <span className="text-sm">{cfg.emoji}</span>
                          <h3 className="text-sm font-semibold text-slate-700">{cfg.label}</h3>
                          <Badge variant="secondary" className="text-xs">{list.length}</Badge>
                        </div>
                        <div className="space-y-3">
                          {list.map((study) => (
                            <div key={study.id} data-testid={`study-card-${study.id}`} className="p-4 bg-white border border-purple-100 rounded-lg">
                              {editingStudyId === study.id ? (
                                <StudyEditForm 
                                  study={study} 
                                  onSave={(data) => handleUpdateStudy(study.id, data)}
                                  onCancel={() => setEditingStudyId(null)}
                                />
                              ) : (
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-medium text-slate-900">{study.study_name}</h4>
                                      <Badge className={cn(
                                        "text-xs",
                                        statusKey === "Ongoing" && "bg-emerald-100 text-emerald-800",
                                        statusKey === "Actively Pursuing" && "bg-amber-100 text-amber-800",
                                        statusKey === "Potential" && "bg-blue-100 text-blue-800",
                                        statusKey === "Concluded" && "bg-slate-200 text-slate-700"
                                      )}>
                                        {study.status}
                                      </Badge>
                                    </div>
                                    <div className="text-sm text-slate-500 mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                                      {study.protocol_number && <p><span className="text-slate-400">Protocol:</span> {study.protocol_number}</p>}
                                      {study.sponsor && <p><span className="text-slate-400">Sponsor:</span> {study.sponsor}</p>}
                                      {study.therapeutic_area && <p><span className="text-slate-400">Area:</span> {study.therapeutic_area}</p>}
                                      {study.feasibility_received_date && (
                                        <p><span className="text-slate-400">Feas. Received:</span> {formatDateTime(study.feasibility_received_date)}</p>
                                      )}
                                      {study.feasibility_completed_date && (
                                        <p><span className="text-slate-400">Feas. Completed:</span> {formatDateTime(study.feasibility_completed_date)}</p>
                                      )}
                                      {study.start_date && <p><span className="text-slate-400">Started:</span> {formatDateTime(study.start_date)}</p>}
                                      {study.end_date && <p><span className="text-slate-400">Ended:</span> {formatDateTime(study.end_date)}</p>}
                                    </div>
                                    {study.notes && (
                                      <p className="text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded italic">&quot;{study.notes}&quot;</p>
                                    )}
                                    {statusKey === "Concluded" && (study.retrospective_notes || study.responsiveness_rating) && (
                                      <div className="mt-2 p-2 bg-slate-100 rounded border border-slate-200">
                                        <p className="text-xs font-semibold text-slate-600 mb-1">Retrospective</p>
                                        {study.responsiveness_rating && (
                                          <p className="text-xs text-slate-700">Responsiveness: {"⭐".repeat(study.responsiveness_rating)} ({study.responsiveness_rating}/5)</p>
                                        )}
                                        {study.retrospective_notes && (
                                          <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{study.retrospective_notes}</p>
                                        )}
                                      </div>
                                    )}
                                    {study.linked_lead_ids && study.linked_lead_ids.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-1">
                                        <span className="text-xs text-slate-500">Linked contacts:</span>
                                        {study.linked_lead_ids.map(l => (
                                          <Badge key={l.lead_id} variant="outline" className="text-xs">
                                            {l.relationship}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-1 ml-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditingStudyId(study.id)}
                                      data-testid={`study-edit-btn-${study.id}`}
                                      title="Edit study"
                                      className="text-slate-400 hover:text-blue-500"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteStudy(study.id)}
                                      data-testid={`study-delete-btn-${study.id}`}
                                      className="text-slate-400 hover:text-red-500"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* BD Opportunities Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-slate-400" />
                    BD Opportunities
                  </CardTitle>
                  <CardDescription>Business development opportunities for this company</CardDescription>
                </div>
                <Link to="/opportunities">
                  <Button variant="outline" size="sm">
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {opportunities.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Target className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p>No opportunities found for this company</p>
                  <Link to="/opportunities">
                    <Button variant="link" size="sm" className="mt-2">
                      Search for opportunities →
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-auto">
                  {opportunities.map((opp) => (
                    <div key={opp.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{opp.event_type}</span>
                          <Badge variant="outline" className="text-xs">{opp.current_phase || "N/A"}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{opp.indication || "No indication"}</p>
                        <p className="text-xs text-slate-400">{opp.source} • {opp.created_at ? new Date(opp.created_at).toLocaleDateString() : "—"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={cn(
                          "text-xs",
                          opp.action_status === "New" && "bg-blue-100 text-blue-700",
                          opp.action_status === "Watching" && "bg-purple-100 text-purple-700",
                          opp.action_status === "Contacted" && "bg-green-100 text-green-700"
                        )}>
                          {opp.action_status}
                        </Badge>
                        {opp.source_url && (
                          <a href={opp.source_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Success Management Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-slate-400" />
                Customer Success Management
              </CardTitle>
              <CardDescription>Track meetings and account relationship</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="meeting-date">Last Meeting Date</Label>
                  <Input
                    id="meeting-date"
                    type="date"
                    value={csm.last_meeting_date || ""}
                    onChange={(e) => setCsm({...csm, last_meeting_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meeting-type">Meeting Type</Label>
                  <Select 
                    value={csm.meeting_type || ""} 
                    onValueChange={(value) => setCsm({...csm, meeting_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Quarterly Meeting">Quarterly Meeting</SelectItem>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Conference">Conference</SelectItem>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="csm-notes">Add CSM Note</Label>
                <Textarea
                  id="csm-notes"
                  placeholder="What was discussed in this meeting / interaction..."
                  value={newCsmNote}
                  onChange={(e) => setNewCsmNote(e.target.value)}
                  rows={3}
                  data-testid="csm-note-input"
                />
                <p className="text-xs text-slate-400">Each save adds a new entry to the history below. Meeting date & type above will tag this note.</p>
              </div>
              <Button 
                onClick={handleSaveCsm}
                disabled={savingCsm}
                className="bg-[#2E7AD1] hover:bg-[#2563EB]"
                data-testid="save-csm-btn"
              >
                {savingCsm ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {newCsmNote.trim() ? "Add Note & Save" : "Save CSM Info"}
              </Button>
              {/* CSM Notes History — log style with click-to-expand */}
              {csm.csm_notes_history && csm.csm_notes_history.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm font-medium text-slate-600 mb-3">
                    Notes History ({csm.csm_notes_history.length})
                  </p>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="csm-notes-history">
                    {csm.csm_notes_history.map((entry, idx) => {
                      const isExpanded = expandedCsmNoteIdx === idx;
                      const preview = (entry.text || "").replace(/\s+/g, " ").trim();
                      return (
                        <div
                          key={entry.id || idx}
                          className="bg-slate-50 rounded-lg border border-slate-100 hover:border-[#2E7AD1] transition-colors"
                          data-testid={`csm-note-${idx}`}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedCsmNoteIdx(isExpanded ? null : idx)}
                            className="w-full text-left p-3"
                          >
                            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                              <span>{entry.saved_at ? new Date(entry.saved_at).toLocaleDateString() : "—"}</span>
                              {entry.meeting_type && (
                                <>
                                  <span className="text-slate-300">|</span>
                                  <span>{entry.meeting_type}</span>
                                </>
                              )}
                              {entry.meeting_date && (
                                <>
                                  <span className="text-slate-300">|</span>
                                  <span>Meeting: {new Date(entry.meeting_date).toLocaleDateString()}</span>
                                </>
                              )}
                              {entry.legacy && (
                                <span className="ml-auto text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">legacy</span>
                              )}
                            </div>
                            {isExpanded ? (
                              <p className="text-slate-700 whitespace-pre-wrap text-sm">{entry.text}</p>
                            ) : (
                              <p className="text-slate-700 text-sm truncate">{preview || "(empty note)"}</p>
                            )}
                          </button>
                          {isExpanded && entry.id && !entry.legacy && (
                            <div className="px-3 pb-2 flex justify-end">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleDeleteCsmNote(entry.id); }}
                                className="text-xs text-red-500 hover:text-red-700"
                                data-testid={`delete-csm-note-${idx}`}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Conversations Panel */}
          <EmailThreadsPanel
            companyName={companyName}
            title="Email Correspondence"
            description="All emails with this company's contacts and matching email domains"
          />

          {/* Company Notes Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-slate-400" />
                Company Notes
              </CardTitle>
              <CardDescription>Track account-level interactions and insights</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a company note..."
                  rows={2}
                  className="flex-1"
                  data-testid="company-note-input"
                />
                <Button 
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="bg-[#2E7AD1] hover:bg-[#2563EB]"
                  data-testid="add-company-note-btn"
                >
                  {addingNote ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <ScrollArea className="h-[300px]">
                {notes.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No company notes yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div 
                        key={note.id}
                        className="p-4 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {note.text}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                              <User className="h-3 w-3" />
                              <span>{note.created_by}</span>
                              <span>•</span>
                              <Clock className="h-3 w-3" />
                              <span>{formatDateTime(note.created_at)}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Contacts */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-slate-400" />
                Contacts ({company.lead_count})
              </CardTitle>
              <CardDescription>⭐ Starred contacts appear first. Click to view details.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="divide-y">
                  {company.leads?.map((lead) => {
                    const remarkStyle = CONTACT_REMARK_COLORS[lead.contact_remark_color];
                    return (
                      <Link 
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        className="block"
                      >
                        <div className={cn(
                          "p-4 hover:bg-slate-50 transition-colors",
                          lead.is_starred && "bg-yellow-50/50"
                        )}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {lead.is_starred && (
                                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                                )}
                                <p className="font-medium text-slate-900 truncate">
                                  {lead.first_name || lead.contact || "N/A"} {lead.last_name || ""}
                                </p>
                                {lead.contact_remark_color && remarkStyle && (
                                  <span className={cn(
                                    "w-2 h-2 rounded-full flex-shrink-0",
                                    remarkStyle.bg.replace("100", "500")
                                  )} title={lead.contact_remark || remarkStyle.label} />
                                )}
                              </div>
                              <p className="text-xs text-slate-500 truncate">{lead.role || "No role"}</p>
                              {lead.email && (
                                <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                                  <Mail className="h-3 w-3" />
                                  <span className="truncate">{lead.email}</span>
                                </div>
                              )}
                              {lead.contact_remark && (
                                <Badge 
                                  variant="outline" 
                                  className={cn("text-xs mt-1", remarkStyle?.bg, remarkStyle?.text)}
                                >
                                  {lead.contact_remark}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {lead.status && typeof lead.status === 'string' && (
                                <Badge variant="outline" className="text-xs">
                                  {lead.status.slice(0, 12)}
                                </Badge>
                              )}
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
